// The CONTEXTUAL right pane of the immersive Git client (design 2.4): it shows
// staging when the working tree is the focus, and full commit detail when a
// commit is selected in the center graph.
//
//   selection = { kind: 'commit', hash }        -> GitCommitDetailView
//   selection = 'working'                        -> GitStagingView (staging)
//   selection = null  AND  workingDirty          -> GitStagingView (default)
//   selection = null  AND  clean                 -> empty hint
//
// The null+dirty default means that, before the user has clicked anything, a
// repo with uncommitted changes lands directly on the staging view (the common
// case), while a clean repo shows a hint to pick a commit or start editing.
//
// The 'working' state uses the GitKraken-style GitStagingView (slice 9b); the
// older SourceControlView still backs the sidebar GitPanel until task 12.

import type { ReactElement } from 'react';
import type { GitApi } from '@slate/core';
import { GitCommitHorizontal } from 'lucide-react';
import { GitStagingView } from './GitStagingView';
import { GitCommitDetailView } from './GitCommitDetailView';
import type { GitSelection } from './GitGraphView';

export interface GitCommitPanelProps {
  /** The repo to operate on, or null when no repo resolves. */
  root: string | null;
  /** The platform GitApi (guarded; undefined when unavailable). */
  git: GitApi | undefined;
  /** What the center graph currently has selected. */
  selection: GitSelection;
  /** Bumped by the client to force a re-read after a cross-pane mutation. */
  refreshToken: number;
  /** Notify the client a mutation happened so the other panes refresh. */
  onMutated: () => void;
  /** Whether the working tree has changes (drives the null-selection default). */
  workingDirty: boolean;
}

export function GitCommitPanel({
  root,
  git,
  selection,
  refreshToken,
  onMutated,
  workingDirty,
}: GitCommitPanelProps): ReactElement {
  if (selection != null && selection !== 'working') {
    return <GitCommitDetailView root={root} git={git} hash={selection.hash} />;
  }

  if (selection === 'working' || workingDirty) {
    return (
      <GitStagingView
        root={root}
        git={git}
        refreshToken={refreshToken}
        onMutated={onMutated}
      />
    );
  }

  return (
    <div className="mp-git__empty">
      <GitCommitHorizontal size={24} aria-hidden className="mp-git__empty-icon" />
      <p className="mp-git__empty-title">No commit selected</p>
      <p className="mp-git__empty-note">
        Select a commit to see details, or make changes to commit.
      </p>
    </div>
  );
}
